import { CommandBar, type ICommandBarItemProps } from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@auth';

/**
 * Navigation bar component with logo, menu items, and user profile
 */
export function Navigation() {
  const navigate = useNavigate();
  const { user, logout, refreshToken } = useAuth();

  const logo = (
    <div
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '4px',
        padding: '4px 12px',
        margin: '4px 8px',
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0
      }}
    >
      <img
        src="https://cdn.net-cloud.com/api/files/resize?path=Logos%2Fnt_logo_horiz.png&height=60"
        alt="Networking Technologies"
        style={{ height: '52px', cursor: 'pointer', display: 'block' }}
        onClick={() => navigate('/')}
      />
    </div>
  );

  const hasAnyRole = (roles: string[]): boolean => {
    if (!roles || roles.length === 0) return true; // No roles required
    if (!user?.roles) return false;
    return roles.some(role => user.roles?.includes(role));
  };

  // Helper function to check if user is authorized for Reports
  const isAuthorizedForReports = (): boolean => {
    // Check if user has Developer role
    if (hasAnyRole(['NtForms.Developer'])) return true;
    
    // Check if user is in authorized UPN list
    const authorizedUpns = ['wdearment@net-cloud.com', 'tcorapi@net-cloud.com', 'jbahm@net-cloud.com', 'dkretz@net-cloud.com'];
    return authorizedUpns.some(upn => user?.username?.toLowerCase() === upn.toLowerCase());
  };

  const menuItems: ICommandBarItemProps[] = [
    {
      key: 'home',
      text: 'Home',
      iconProps: { iconName: 'Home' },
      onClick: () => navigate('/'),
    },
    {
      key: 'reports',
      text: 'Reports',
      iconProps: { iconName: 'ReportDocument' },
      subMenuProps: {
        items: [
          {
            key: 'maintenanceSupportRevenue',
            text: 'Maintenance & Support Revenue',
            iconProps: { iconName: 'Money' },
            onClick: () => navigate('/reports/maintenance-support-revenue'),
          },
        ],
      },
    },
    {
      key: 'apps',
      text: 'Apps',
      iconProps: { iconName: 'AppIconDefault' },
      subMenuProps: {
        items: [
          {
            key: 'absenceRequests',
            text: 'Absence Requests',
            iconProps: { iconName: 'Calendar' },
            subMenuProps: {
              items: [
                {
                  key: 'viewRequests',
                  text: 'View Requests',
                  iconProps: { iconName: 'View' },
                  onClick: () => navigate('/absence-request'),
                },
                {
                  key: 'newRequest',
                  text: 'New Request',
                  iconProps: { iconName: 'Add' },
                  onClick: () => navigate('/absence-request'),
                },
              ],
            },
          },
          {
            key: 'employees',
            text: 'Employees',
            iconProps: { iconName: 'People' },
            subMenuProps: {
              items: [
                {
                  key: 'employeeForms',
                  text: 'Employee Forms',
                  iconProps: { iconName: 'ContactCard' },
                  onClick: () => navigate('/employee-forms'),
                },
                {
                  key: 'addEmployee',
                  text: 'Add Employee',
                  iconProps: { iconName: 'AddFriend' },
                  onClick: () => navigate('/employee-forms'),
                },
                {
                  key: 'termEmployee',
                  text: 'Term Employee',
                  iconProps: { iconName: 'UserRemove' },
                  onClick: () => navigate('/employee-forms'),
                },
                {
                  key: 'updateEmployee',
                  text: 'Update Employee',
                  iconProps: { iconName: 'EditContact' },
                  onClick: () => navigate('/employee-update'),
                },
              ],
            },
          },
          {
            key: 'serviceTime',
            text: 'Service Time Analysis',
            iconProps: { iconName: 'Clock' },
            onClick: () => navigate('/service-time-analysis'),
          },
          {
            key: 'developmentDashboard',
            text: 'Development Dashboard',
            iconProps: { iconName: 'DeveloperTools' },
            onClick: () => navigate('/development-dashboard'),
            requiredRoles: ['NtForms.Developer', 'COO']
          },
          {
            key: 'salesHeatMap',
            text: 'Sales Heat Map',
            iconProps: { iconName: 'WorldClock' },
            onClick: () => navigate('/salesheatmap'),
          },
          {
            key: 'salesDashboard',
            text: 'Sales Dashboard',
            iconProps: { iconName: 'Money' },
            onClick: () => navigate('/sales-dashboard'),
          },
          {
            key: 'ntgoalDashboard',
            text: 'Nt Goal Dashboard',
            iconProps: { iconName: 'BarChartVertical' },
            onClick: () => navigate('/ntgoal-dashboard'),
          },
          {
            key: 'redGreen',
            text: 'Red/Green Credit Check',
            iconProps: { iconName: 'Lightbulb' },
            onClick: () => navigate('/red-green'),
          },
          {
            key: 'lokiAi',
            text: 'Loki AI Chat',
            iconProps: { iconName: 'ChatBot' },
            onClick: () => navigate('/loki'),
          },
          {
            key: 'nettechChat',
            text: 'Nettech Chat',
            iconProps: { iconName: 'Comment' },
            onClick: () => navigate('/chat'),
          },
          {
            key: 'purchaseRequest',
            text: 'Purchase Request',
            iconProps: { iconName: 'ShoppingCart' },
            onClick: () => navigate('/purchase-request'),
          },
          {
            key: 'eolEos',
            text: 'EOL/EOS Tracking',
            iconProps: { iconName: 'DateTime' },
            onClick: () => navigate('/eol-eos'),
          },
          {
            key: 'inventory',
            text: 'Inventory Management',
            iconProps: { iconName: 'Database' },
            onClick: () => navigate('/inventory'),
          },
          {
            key: 'onCallManager',
            text: 'On-Call Manager',
            iconProps: { iconName: 'Ringer' },
            onClick: () => navigate('/oncall-manager'),
          },
        ].filter(item => {
          // Always show absence requests and employees
          if (item.key === 'absenceRequests') return true;
          if (item.key === 'employees') return hasAnyRole(['Submit.EmployeeForm']);
          if (item.key === 'serviceTime') return hasAnyRole(['ServiceTimeAnalysis.Viewer', 'ServiceDeliveryManager', 'NtForms.Developer']);
          if (item.key === 'salesHeatMap') return hasAnyRole(['SalesHeatMap.Write', 'SalesHeatMap.Admin', 'NtForms.Developer']);
          if (item.key === 'salesDashboard') return true; // Available to all (backend enforces territory)
          if (item.key === 'ntgoalDashboard') return hasAnyRole(['NtForms.Developer', 'NettechGoals.Viewer']);
          if (item.key === 'redGreen') return true; // Available to all
          if (item.key === 'lokiAi') return true; // Available to all
          if (item.key === 'nettechChat') return true; // Available to all
          if (item.key === 'purchaseRequest') return hasAnyRole(['NtForms.Developer']); // In development, developers only
          if (item.key === 'eolEos') return hasAnyRole(['NtForms.Developer', 'EolEos.Read', 'EolEos.Write', 'EolEos.Import']);
          if (item.key === 'inventory') return hasAnyRole(['NtForms.Developer', 'inventory.readonly', 'inventory.manager', 'inventory.admin']);
          if (item.key === 'onCallManager') return true;
          if (item.key === 'developmentDashboard') return hasAnyRole(['NtForms.Developer', 'COO']);
          return false;
        }),
      },
    },
    {
      key: 'tools',
      text: 'Tools',
      iconProps: { iconName: 'Settings' },
      subMenuProps: {
        items: [
          {
            key: 'backlogs',
            text: 'Backlogs',
            iconProps: { iconName: 'BulletedList' },
            onClick: () => navigate('/backlogs'),
          },
          {
            key: 'devPriority',
            text: 'Dev Priority',
            iconProps: { iconName: 'FavoriteStarFill' },
            onClick: () => navigate('/devpriority'),
          },
          {
            key: 'usac',
            text: 'USAC Import',
            iconProps: { iconName: 'Upload' },
            onClick: () => navigate('/usac-import'),
          },
          {
            key: 'clientInformation',
            text: 'Client Information',
            iconProps: { iconName: 'ContactCard' },
            onClick: () => navigate('/clientinformation'),
          },
          {
            key: 'platformMapping',
            text: 'Platform Mapping',
            iconProps: { iconName: 'Link' },
            onClick: () => navigate('/platform-mapping'),
          },
          {
            key: 'companyTeamCleanup',
            text: 'Company Team Cleanup',
            iconProps: { iconName: 'PeopleBlock' },
            onClick: () => navigate('/company-team-cleanup'),
          },
          {
            key: 'employeeUpdate',
            text: 'Employee Update Form',
            iconProps: { iconName: 'EditContact' },
            onClick: () => navigate('/employee-update'),
          },
          {
            key: 'estimateActionPlan',
            text: 'Estimate Action Plan',
            iconProps: { iconName: 'TaskManager' },
            onClick: () => navigate('/estimate-action-plan'),
          },
          {
            key: 'connectwise',
            text: 'ConnectWise Tools',
            iconProps: { iconName: 'ConnectContacts' },
            onClick: () => navigate('/cwtools'),
          },
          {
            key: 'hpeBom',
            text: 'HPE BOM Processing',
            iconProps: { iconName: 'ExcelDocument' },
            onClick: () => navigate('/hpe-bom'),
          },
          {
            key: 'hpSalesTracker',
            text: 'HP Sales Tracker Importer',
            iconProps: { iconName: 'Upload' },
            onClick: () => navigate('/hp-sales-tracker'),
          },
          {
            key: 'docAcceptance',
            text: 'Document Acceptance',
            iconProps: { iconName: 'DocumentApproval' },
            onClick: () => navigate('/acceptancemanager'),
          },
          {
            key: 'reportDocs',
            text: 'Reports Documentation',
            iconProps: { iconName: 'ReportDocument' },
            onClick: () => navigate('/docs'),
          },
          {
            key: 'reportSurvey',
            text: 'Report Usage Survey',
            iconProps: { iconName: 'Questionnaire' },
            onClick: () => navigate('/report-survey'),
          },
          {
            key: 'reportSurveyAdmin',
            text: 'Survey Administration',
            iconProps: { iconName: 'Admin' },
            onClick: () => navigate('/report-survey-admin'),
          },
          {
            key: 'experianExport',
            text: 'Experian Export',
            iconProps: { iconName: 'ExcelDocument' },
            onClick: () => navigate('/experianexport'),
          },
          {
            key: 'opportunityAnalysis',
            text: 'Opportunity Analysis',
            iconProps: { iconName: 'BarChartVertical' },
            onClick: () => navigate('/opportunityanalysis'),
          },
          {
            key: 'keyManagement',
            text: 'Key Management',
            iconProps: { iconName: 'Lock' },
            onClick: () => navigate('/admin/key-management/secrets'),
          },
          {
            key: 'keyVaultPermissions',
            text: 'Key Vault Permissions',
            iconProps: { iconName: 'Permissions' },
            onClick: () => navigate('/admin/key-management/permissions'),
          },
          {
            key: 'ntPass',
            text: 'NtPass Password Manager',
            iconProps: { iconName: 'Lock' },
            onClick: () => navigate('/ntpass'),
          },
        ].filter(item => {
          if (item.key === 'backlogs' || item.key === 'devPriority') return hasAnyRole(['NtForms.Developer']);
          if (item.key === 'usac') return hasAnyRole(['USAC.User']);
          if (item.key === 'clientInformation') return hasAnyRole(['NtForms.Developer']);
          if (item.key === 'platformMapping') return hasAnyRole(['AuvikMapper', 'SentinelMapper', 'NtForms.Developer']);
          if (item.key === 'companyTeamCleanup') return hasAnyRole(['CompanyTeamCleanup', 'NtForms.Developer']);
          if (item.key === 'employeeUpdate') return true; // Available to all
          if (item.key === 'estimateActionPlan') return hasAnyRole(['NtForms.Developer']);
          if (item.key === 'connectwise') return hasAnyRole(['CwTools.View', 'NtForms.Developer']);
          if (item.key === 'hpeBom') return hasAnyRole(['HpeBomProcessing.View', 'NtForms.Developer']);
          if (item.key === 'hpSalesTracker') return hasAnyRole(['HPSalesTrackerImporter', 'NtForms.Developer']);
          if (item.key === 'docAcceptance') return hasAnyRole(['DocumentAcceptance.Manager', 'NtForms.Developer']);
          if (item.key === 'reportDocs') return hasAnyRole(['DevDocs.View', 'NtForms.Developer']);
          if (item.key === 'reportSurvey') return true; // Available to all authenticated users
          if (item.key === 'reportSurveyAdmin') return hasAnyRole(['NtForms.Developer']);
          if (item.key === 'experianExport') return hasAnyRole(['Experian.Exports', 'NtForms.Developer']);
          if (item.key === 'opportunityAnalysis') return hasAnyRole(['OppAnalysis.Reader', 'NtForms.Developer']);
          if (item.key === 'keyManagement') return hasAnyRole(['KeyVault.Admin', 'NtForms.Developer']);
          if (item.key === 'keyVaultPermissions') return hasAnyRole(['KeyVault.Admin', 'NtForms.Developer']);
          if (item.key === 'ntPass') return hasAnyRole(['NtForms.Developer']);
          return false;
        }),
      },
    },
  ].filter(item => {
    // Always show home
    if (item.key === 'home') return true;
    
    // Filter based on roles
    if (item.key === 'reports') return isAuthorizedForReports();
    if (item.key === 'apps') return true; // Apps menu always visible (items filtered internally)
    if (item.key === 'tools') return true; // Tools menu always visible (items filtered internally)
    
    return false;
  });

  const farItems: ICommandBarItemProps[] = [
    {
      key: 'user',
      text: user?.name || user?.username || '',
      iconProps: { iconName: 'Contact' },
      subMenuProps: {
        items: [
          {
            key: 'profile',
            text: user?.email || '',
            iconProps: { iconName: 'Mail' },
            disabled: true,
          },
          {
            key: 'divider1',
            text: '-',
          },
          {
            key: 'refresh',
            text: 'Refresh Permissions',
            iconProps: { iconName: 'Refresh' },
            onClick: () => { void refreshToken(); },
            title: 'Refresh your roles and permissions without logging out',
          },
          {
            key: 'divider2',
            text: '-',
          },
          {
            key: 'logout',
            text: 'Sign Out',
            iconProps: { iconName: 'SignOut' },
            onClick: () => { void logout(); },
          },
        ],
      },
    },
  ];

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '76px' }}>
      {logo}
      <div style={{ flex: 1, minWidth: 0 }}>
        <CommandBar
          items={menuItems}
          farItems={farItems}
          styles={{
            root: {
              padding: 0,
              height: '76px'
            },
            primarySet: {
              minWidth: 0,
              flexGrow: 1,
              overflow: 'hidden'
            },
            secondarySet: {
              flexShrink: 0,
              marginLeft: 'auto'
            }
          }}
        />
      </div>
    </div>
  );
}

export default Navigation;
